import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { WSP_FF1_SKILL_SHEETS } from '../data/skillSheets';
import { SkillSheet } from '../types/wspSkillSheets';

const FlipChartPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [skillSheet, setSkillSheet] = useState<SkillSheet | null>(null);
    const [flippedSteps, setFlippedSteps] = useState<Set<number>>(new Set());

    useEffect(() => {
        if (id) {
            const sheet = WSP_FF1_SKILL_SHEETS.find(s => s.skill_sheet_number.toString() === id);
            setSkillSheet(sheet || null);
        }
    }, [id]);

    const handleStepClick = (stepNumber: number) => {
        setFlippedSteps(prev => {
            const newSet = new Set(prev);
            if (newSet.has(stepNumber)) {
                newSet.delete(stepNumber);
            } else {
                newSet.add(stepNumber);
            }
            return newSet;
        });
    };

    const getVisibleStepsCount = () => {
        if (!skillSheet) return 0;
        return skillSheet.task_steps.length - flippedSteps.size;
    };

    const handleNextSkillSheet = () => {
        if (!skillSheet) return;
        
        // Find next skill sheet
        const currentIndex = WSP_FF1_SKILL_SHEETS.findIndex(s => s.skill_sheet_number === skillSheet.skill_sheet_number);
        const nextIndex = currentIndex + 1;
        
        if (nextIndex < WSP_FF1_SKILL_SHEETS.length) {
            const nextSkillSheet = WSP_FF1_SKILL_SHEETS[nextIndex];
            navigate(`/skill-sheets/${nextSkillSheet.skill_sheet_number}/flip-chart`);
        } else {
            // If it's the last skill sheet, go back to skill sheets list
            navigate('/skill-sheets');
        }
    };

    const handleHideAll = () => {
        setFlippedSteps(new Set());
    };

    if (!skillSheet) {
        return (
            <div className="content-container">
                <div className="glass-header">
                    <h1 className="text-title-lg">Skill Sheet Not Found</h1>
                    <button onClick={() => navigate('/skill-sheets')} className="btn btn-primary">
                        Back to Skill Sheets
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="content-container">
            <div className="glass-header">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h1 className="text-title-lg">Flip Chart Mode</h1>
                    <button onClick={() => navigate('/skill-sheets')} className="btn btn-secondary">
                        Back to Skill Sheets
                    </button>
                </div>
            </div>
            
            {/* Skill Sheet Overview Box */}
            <div className="glass-header" style={{ marginBottom: 'var(--spacing-lg)' }}>
                <div>
                    <h3 className="text-title-lg">
                        Skill Sheet {skillSheet.skill_sheet_number}: {skillSheet.skill_area}
                    </h3>
                    <div style={{ display: 'flex', gap: 'var(--spacing-lg)', marginTop: 'var(--spacing-md)' }}>
                        <span className="text-caption">JPR: {skillSheet.jpr}</span>
                        <span className="text-caption">NFPA {skillSheet.nfpa_standard}</span>
                    </div>
                    {skillSheet.conditions && (
                        <p className="text-body-md" style={{ marginTop: 'var(--spacing-md)' }}>
                            {skillSheet.conditions}
                        </p>
                    )}
                    <div style={{ marginTop: 'var(--spacing-md)' }}>
                        <h4 className="text-title-sm">Task:</h4>
                        <p className="text-body-md">{skillSheet.task}</p>
                    </div>
                    <div style={{ marginTop: 'var(--spacing-md)' }}>
                        <h4 className="text-title-sm">Performance Outcome:</h4>
                        <p className="text-body-md">{skillSheet.performance_outcome}</p>
                    </div>
                </div>
            </div>

            {/* Task Steps */}
            <div className="content-section">
                <h3 className="text-title-md">Task Steps</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
                    {skillSheet.task_steps.map((step) => {
                        const isFlipped = flippedSteps.has(step.number);
                        
                        return (
                            <div key={step.number} className="content-card">
                                {!isFlipped ? (
                                    <div style={{ textAlign: 'center' }}>
                                        <button 
                                            onClick={() => handleStepClick(step.number)}
                                            className="btn btn-secondary"
                                            style={{ width: '100%' }}
                                        >
                                            Click to see answer
                                        </button>
                                    </div>
                                ) : (
                                    <div>
                                        <div style={{ display: 'flex', gap: 'var(--spacing-md)', alignItems: 'flex-start' }}>
                                            <span className="text-title-sm" style={{ 
                                                minWidth: '2rem', 
                                                color: 'var(--primary-color)',
                                                fontWeight: 'bold'
                                            }}>
                                                {step.number}:
                                            </span>
                                            <span className="text-body-md">{step.task_step}</span>
                                        </div>
                                        <button 
                                            onClick={() => handleStepClick(step.number)}
                                            className="btn btn-secondary"
                                            style={{ 
                                                marginTop: 'var(--spacing-md)',
                                                width: '100%'
                                            }}
                                        >
                                            Hide answer
                                        </button>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
                
                {/* Progress indicator */}
                <div style={{ 
                    textAlign: 'center', 
                    marginTop: 'var(--spacing-lg)',
                    padding: 'var(--spacing-md)',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: 'var(--border-radius)'
                }}>
                    <p className="text-body-sm">
                        {getVisibleStepsCount()} of {skillSheet.task_steps.length} steps remaining
                    </p>
                </div>

                {/* Action buttons */}
                <div style={{ 
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 'var(--spacing-md)',
                    marginTop: 'var(--spacing-lg)'
                }}>
                    <button 
                        onClick={handleHideAll}
                        className="btn btn-secondary"
                    >
                        Hide All
                    </button>
                    <button 
                        onClick={handleNextSkillSheet}
                        className="btn btn-primary"
                    >
                        Next
                    </button>
                </div>
            </div>
        </div>
    );
};

export default FlipChartPage;
